import React, { useState, useEffect } from 'react';
import {
  Drawer,
  Form,
  Input,
  Select,
  Button,
  InputNumber,
  message,
  Row,
  Col,
  Card,
  Divider,
  Tag,
  Checkbox,
} from 'antd';
import { PlusOutlined, WalletOutlined, LinkOutlined, DeleteOutlined, ExclamationCircleOutlined, CalendarOutlined, SwapOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import {
  ExpenseCategory,
  ExpenseTransaction,
  getExpenseCategories,
  createExpenseTransaction,
  updateExpenseTransaction,
  createExpenseCategory,
  // Thêm các import mới cho wallet linking
  SourceWallet,
  WalletLink,
  getAvailableWallets,
  getExpenseWalletLinks,
  linkExpenseToWallet,
  deleteExpenseWalletLink,
  formatCurrency,
} from '../../services/expenseService';
// Import service chuyển tiền
import { incomeService } from '../../services/incomeService';
import { DatePickerModal } from '../common/DatePickerModal';
import CategoriesModalMobile from './CategoriesModalMobie';
import { SellectWalletModalMobile } from '../income/SellectWalletModalMobile';
import styles from '../common/DrawerModalMobile.module.css';

interface ExpenseTransactionModalProps {
  isOpen: boolean;
  onClose: () => void;
  transaction?: ExpenseTransaction;
  onSuccess?: () => void;
}

const CATEGORY_ICONS = [
  '🍽️', '🚗', '🛒', '📄', '🎮', '🏥', '📚', '💡', '🏠', '👕', '🎬', '✈️', '💊', '🎨', '🏋️', '📱'
];

const CATEGORY_COLORS = [
  '#EF4444', '#3B82F6', '#10B981', '#F59E0B', '#8B5CF6', '#EC4899', '#06B6D4', '#6B7280',
  '#84CC16', '#F97316', '#6366F1', '#14B8A6', '#F43F5E', '#A855F7', '#EAB308', '#22C55E'
];

export const ExpenseTransactionModalMobile: React.FC<ExpenseTransactionModalProps> = ({
  isOpen,
  onClose,
  transaction,
  onSuccess,
}) => {
  const [categories, setCategories] = useState<ExpenseCategory[]>([]);
  const [wallets, setWallets] = useState<SourceWallet[]>([]);
  const [walletLinks, setWalletLinks] = useState<WalletLink[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingWallets, setLoadingWallets] = useState(false);
  const [form] = Form.useForm();
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showCategoriesModal, setShowCategoriesModal] = useState(false);
  const [categoryForm] = Form.useForm();
  const [selectedWalletId, setSelectedWalletId] = useState<number | undefined>();
  const [linkAmount, setLinkAmount] = useState<number>(0);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Dayjs>(dayjs());
  const [selectedCategory, setSelectedCategory] = useState<ExpenseCategory | null>(null);
  const [showWalletModal, setShowWalletModal] = useState(false);
  const [selectedWallet, setSelectedWallet] = useState<SourceWallet | null>(null);
  
  // Thêm state cho chức năng chuyển tiền
  const [isTransferTransaction, setIsTransferTransaction] = useState(false);
  const [fromWalletId, setFromWalletId] = useState<number | undefined>();
  const [toWalletId, setToWalletId] = useState<number | undefined>();
  const [transferAmount, setTransferAmount] = useState<number>(0);

  const isEdit = !!transaction;

  useEffect(() => {
    if (isOpen) {
      loadCategories();
      loadWallets();
      if (transaction) {
        const transactionDate = dayjs(transaction.expense_date);
        setSelectedDate(transactionDate);
        form.setFieldsValue({
          category_id: transaction.category_id,
          amount: transaction.amount,
          expense_date: transactionDate,
          notes: transaction.notes || '',
        });
        loadWalletLinks();
        setLinkAmount(transaction.amount);
      } else {
        const today = dayjs();
        setSelectedDate(today);
        form.resetFields();
        form.setFieldsValue({
          expense_date: today,
        });
        setWalletLinks([]);
        setLinkAmount(0);
        setSelectedCategory(null);
        setSelectedWallet(null);
        // Reset transfer state
        setIsTransferTransaction(false);
        setFromWalletId(undefined);
        setToWalletId(undefined);
        setTransferAmount(0);
      }
    } else {
      // Reset khi modal đóng
      setSelectedWallet(null);
      setSelectedWalletId(undefined);
      setShowCategoryModal(false);
      setShowCategoriesModal(false);
      setShowDatePicker(false);
      setShowWalletModal(false);
    }
  }, [isOpen, transaction, form]);

  // Cập nhật selectedCategory khi categories được load và có transaction
  useEffect(() => {
    if (transaction && categories.length > 0) {
      const foundCategory = categories.find(cat => cat.id === transaction.category_id);
      setSelectedCategory(foundCategory || null);
    }
  }, [categories, transaction]);

  // Cập nhật selectedWallet khi wallets được load và có transaction
  useEffect(() => {
    if (transaction && wallets.length > 0) {
      // Tìm ví đầu tiên trong walletLinks để hiển thị
      if (walletLinks.length > 0) {
        const firstLink = walletLinks[0];
        const foundWallet = wallets.find(w => w.id === firstLink.wallet_id);
        if (foundWallet) {
          setSelectedWallet(foundWallet);
          setSelectedWalletId(foundWallet.id);
        }
      }
    }
  }, [transaction, wallets, walletLinks]);

  const loadCategories = async () => {
    try {
      const categoriesData = await getExpenseCategories();
      setCategories(categoriesData);
    } catch (error) {
      message.error('Không thể tải danh mục chi tiêu');
    }
  };

  const loadWallets = async () => {
    try {
      setLoadingWallets(true);
      const walletsData = await getAvailableWallets();
      setWallets(walletsData.filter(wallet => wallet.is_active));
    } catch (error) {
      message.error('Không thể tải danh sách ví');
    } finally {
      setLoadingWallets(false);
    }
  };

  const loadWalletLinks = async () => {
    if (!transaction) return;
    try {
      const links = await getExpenseWalletLinks(transaction.id);
      setWalletLinks(links);
    } catch (error) {
      console.error('Error loading wallet links:', error);
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      // Nếu là giao dịch chuyển tiền
      if (isTransferTransaction) {
        if (!fromWalletId || !toWalletId || transferAmount <= 0) {
          message.error('Vui lòng chọn ví chuyển, ví nhận và nhập số tiền hợp lệ');
          return;
        }

        if (fromWalletId === toWalletId) {
          message.error('Không thể chuyển tiền trong cùng một ví');
          return;
        }

        // Thực hiện chuyển tiền
        await incomeService.transferWallet({
          from_wallet_id: fromWalletId,
          to_wallet_id: toWalletId,
          amount: transferAmount,
          transfer_date: values.expense_date.format('YYYY-MM-DD'),
          notes: values.notes || '',
        });

        message.success('Chuyển tiền thành công! Đã tạo giao dịch chi tiêu ở ví chuyển và giao dịch thu nhập ở ví nhận.');
        onSuccess?.();
        onClose();
        
        // Thêm delay nhỏ để đảm bảo dữ liệu được cập nhật
        setTimeout(() => {
          window.location.reload();
        }, 100);
        return;
      }

      // Xử lý giao dịch chi tiêu thông thường
      const submitData = {
        category_id: values.category_id,
        amount: values.amount,
        expense_date: values.expense_date.format('YYYY-MM-DD'),
        notes: values.notes || '',
      };

      let createdTransaction: ExpenseTransaction;

      if (isEdit && transaction) {
        createdTransaction = await updateExpenseTransaction(transaction.id, submitData);
        message.success('Cập nhật giao dịch thành công');
      } else {
        createdTransaction = await createExpenseTransaction(submitData);
        message.success('Thêm giao dịch thành công');
      }

      // Nếu có chọn ví và số tiền liên kết, thực hiện liên kết
      if (selectedWalletId && linkAmount > 0) {
        try {
          await linkExpenseToWallet(createdTransaction.id, selectedWalletId, linkAmount);
          message.success('Liên kết ví thành công');
        } catch (error) {
          message.warning('Giao dịch đã tạo nhưng liên kết ví thất bại');
        }
      }
      
      // Gọi callback onSuccess để refresh màn hình gốc
      onSuccess?.();
      onClose();
      
      // Thêm delay nhỏ để đảm bảo dữ liệu được cập nhật
      setTimeout(() => {
        window.location.reload();
      }, 100);
    } catch (error: any) {
      if (error.errorFields) {
        message.error('Vui lòng nhập đầy đủ thông tin');
      } else {
        // Hiển thị thông báo chi tiết từ API
        const errorMessage = error.response?.data?.error || error.message || 'Có lỗi xảy ra khi lưu giao dịch';
        message.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLinkWallet = async () => {
    if (!transaction || !selectedWalletId || linkAmount <= 0) {
      message.error('Vui lòng chọn ví và nhập số tiền hợp lệ');
      return;
    }

    try {
      setLoading(true);
      await linkExpenseToWallet(transaction.id, selectedWalletId, linkAmount);
      message.success('Liên kết ví thành công');
      loadWalletLinks();
      setSelectedWalletId(undefined);
      setLinkAmount(0);
    } catch (error) {
      message.error('Có lỗi xảy ra khi liên kết ví');
    } finally {
      setLoading(false);
    }
  };

  const handleUnlinkWallet = async (linkId: number) => {
    if (!transaction) return;

    try {
      setLoading(true);
      await deleteExpenseWalletLink(transaction.id, linkId);
      message.success('Hủy liên kết ví thành công');
      loadWalletLinks();
    } catch (error) {
      message.error('Có lỗi xảy ra khi hủy liên kết ví');
    } finally {
      setLoading(false);
    }
  };

  const handleAddCategory = async () => {
    try {
      const values = await categoryForm.validateFields();
      setLoading(true);

      await createExpenseCategory({
        name: values.name,
        icon: values.icon,
        color: values.color,
      });

      setShowCategoryModal(false);
      categoryForm.resetFields();
      await loadCategories();
      message.success('Thêm danh mục thành công');
    } catch (error: any) {
      if (error.errorFields) {
        message.error('Vui lòng nhập tên danh mục');
      } else {
        message.error('Có lỗi xảy ra khi thêm danh mục');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSelectCategory = (category: any) => {
    console.log('Selected category:', category);
    const categoryId = parseInt(category.id);
    form.setFieldsValue({ category_id: categoryId });
    
    // Tìm và lưu thông tin danh mục đã chọn
    const foundCategory = categories.find(cat => cat.id === categoryId);
    setSelectedCategory(foundCategory || null);
    
    setShowCategoriesModal(false);
  };

  const handleCreateNewCategory = () => {
    setShowCategoriesModal(false);
    setShowCategoryModal(true);
  };

  const handleDateSelect = (date: Dayjs) => {
    setSelectedDate(date);
    form.setFieldsValue({ expense_date: date });
    setShowDatePicker(false);
  };

  const handleWalletSelect = (wallet: SourceWallet) => {
    setSelectedWallet(wallet);
    setSelectedWalletId(wallet.id);
  };

  const getWalletDisplayName = (wallet: SourceWallet) => {
    return `${wallet.name} (${wallet.source_name})`;
  };

  const getWalletBalanceDisplay = (wallet: SourceWallet) => {
    return `${formatCurrency(wallet.current_balance)}`;
  };

  // Hàm kiểm tra ví có đủ tiền để chuyển không
  const checkWalletBalance = (walletId: number, amount: number) => {
    const wallet = wallets.find(w => w.id === walletId);
    return wallet && wallet.current_balance >= amount;
  };

  return (
    <>
      <Drawer
        title={isEdit ? 'Chỉnh sửa giao dịch' : 'Thêm giao dịch mới'}
        open={isOpen}
        onClose={onClose}
        placement="bottom"
        height="85vh"
        className={styles.drawer}
        bodyStyle={{ 
          padding: 0,
          userSelect: 'none',
          WebkitUserSelect: 'none',
          MozUserSelect: 'none',
          msUserSelect: 'none',
          WebkitTouchCallout: 'none',
          WebkitTapHighlightColor: 'transparent'
        }}
      >
        <div className={styles.drawerContent}>
          <div className={styles.drawerBody}>
            <Form
              form={form}
              layout="vertical"
              initialValues={{
                expense_date: dayjs(),
              }}
            >
              {/* Checkbox chuyển tiền */}
              <Form.Item className={styles.formItem}>
                <Checkbox
                  checked={isTransferTransaction}
                  onChange={(e) => setIsTransferTransaction(e.target.checked)}
                  className={styles.transferCheckbox}
                >
                  <SwapOutlined /> Là giao dịch chuyển tiền
                </Checkbox>
              </Form.Item>

                             {/* Form chuyển tiền */}
               {isTransferTransaction && (
                 <div className={styles.transferSection}>
                   <Divider orientation="left">
                     <SwapOutlined /> Thông tin chuyển tiền
                   </Divider>
                   
                   <div className={styles.transferInfo}>
                     <p style={{ fontSize: '14px', color: '#666', marginBottom: '16px' }}>
                       💡 <strong>Lưu ý:</strong> Khi chuyển tiền, hệ thống sẽ tự động tạo:
                       <br />• Giao dịch chi tiêu ở ví chuyển (category: Chuyển tiền)
                       <br />• Giao dịch thu nhập ở ví nhận
                     </p>
                   </div>
                  
                  <Form.Item
                    name="transfer_amount"
                    label="Số tiền chuyển"
                    rules={[{ required: true, message: 'Vui lòng nhập số tiền' }]}
                    className={styles.formItem}
                  >
                    <InputNumber
                      className={styles.inputNumber}
                      placeholder="Nhập số tiền chuyển"
                      min={0}
                      formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                      inputMode="numeric"
                      pattern="[0-9]*"
                      value={transferAmount}
                      onChange={(value) => setTransferAmount(value || 0)}
                    />
                  </Form.Item>

                  <Form.Item
                    name="from_wallet"
                    label="Ví chuyển"
                    rules={[{ required: true, message: 'Vui lòng chọn ví chuyển' }]}
                    className={styles.formItem}
                  >
                    <Select
                      className={styles.select}
                      placeholder="Chọn ví chuyển"
                      value={fromWalletId}
                      onChange={setFromWalletId}
                      loading={loadingWallets}
                    >
                      {wallets.map((wallet) => (
                        <Select.Option key={wallet.id} value={wallet.id}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span>{getWalletDisplayName(wallet)}</span>
                            <span style={{ color: '#666', fontSize: '12px' }}>
                              {getWalletBalanceDisplay(wallet)}
                            </span>
                          </div>
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>

                  <Form.Item
                    name="to_wallet"
                    label="Ví nhận"
                    rules={[{ required: true, message: 'Vui lòng chọn ví nhận' }]}
                    className={styles.formItem}
                  >
                    <Select
                      className={styles.select}
                      placeholder="Chọn ví nhận"
                      value={toWalletId}
                      onChange={setToWalletId}
                      loading={loadingWallets}
                    >
                      {wallets.map((wallet) => (
                        <Select.Option key={wallet.id} value={wallet.id}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span>{getWalletDisplayName(wallet)}</span>
                            <span style={{ color: '#666', fontSize: '12px' }}>
                              {getWalletBalanceDisplay(wallet)}
                            </span>
                          </div>
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>

                  <Form.Item
                    name="expense_date"
                    label="Ngày chuyển tiền"
                    rules={[{ required: true, message: 'Vui lòng chọn ngày' }]}
                    className={styles.formItem}
                  >
                    <div className={styles.dateInputWrapper}>
                      <Input
                        className={styles.dateInput}
                        placeholder="Chọn ngày"
                        value={selectedDate.format('DD/MM/YYYY')}
                        readOnly
                        onClick={() => setShowDatePicker(true)}
                        suffix={<CalendarOutlined style={{ color: '#666' }} />}
                      />
                    </div>
                  </Form.Item>

                  <Form.Item
                    name="notes"
                    label="Ghi chú"
                    className={styles.formItem}
                  >
                    <Input.TextArea
                      className={styles.textArea}
                      placeholder="Ghi chú (tùy chọn)"
                      rows={3}
                    />
                  </Form.Item>

                  {/* Hiển thị cảnh báo nếu ví chuyển không đủ tiền */}
                  {fromWalletId && transferAmount > 0 && !checkWalletBalance(fromWalletId, transferAmount) && (
                    <div className={styles.amountError}>
                      <ExclamationCircleOutlined />
                      Số dư ví chuyển không đủ để thực hiện chuyển khoản
                    </div>
                  )}

                  {/* Hiển thị cảnh báo nếu chọn cùng một ví */}
                  {fromWalletId && toWalletId && fromWalletId === toWalletId && (
                    <div className={styles.amountError}>
                      <ExclamationCircleOutlined />
                      Không thể chuyển tiền trong cùng một ví
                    </div>
                  )}
                </div>
              )}

              {/* Form giao dịch chi tiêu thông thường */}
              {!isTransferTransaction && (
                <>
                  <Form.Item
                    name="amount"
                    label="Số tiền"
                    rules={[{ required: true, message: 'Vui lòng nhập số tiền' }]}
                    className={styles.formItem}
                  >
                    <InputNumber
                      className={styles.inputNumber}
                      placeholder="Nhập số tiền"
                      min={0}
                      formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                      inputMode="numeric"
                      pattern="[0-9]*"
                      onChange={(value) => {
                        if (value) {
                          setLinkAmount(value);
                        }
                      }}
                    />
                  </Form.Item>

                  <Form.Item
                    name="category_id"
                    label="Danh mục"
                    rules={[{ required: true, message: 'Vui lòng chọn danh mục' }]}
                    className={styles.formItem}
                  >
                    <div className={styles.dateInputWrapper}>
                      <Input
                        className={styles.dateInput}
                        placeholder="Chọn danh mục"
                                                 value={selectedCategory ? `${selectedCategory.icon} ${selectedCategory.name}` : ''}
                        readOnly
                        onClick={() => setShowCategoriesModal(true)}
                        suffix={<PlusOutlined style={{ color: '#666' }} />}
                      />
                    </div>
                  </Form.Item>

                  <Form.Item
                    name="expense_date"
                    label="Ngày chi tiêu"
                    rules={[{ required: true, message: 'Vui lòng chọn ngày' }]}
                    className={styles.formItem}
                  >
                    <div className={styles.dateInputWrapper}>
                      <Input
                        className={styles.dateInput}
                        placeholder="Chọn ngày"
                        value={selectedDate.format('DD/MM/YYYY')}
                        readOnly
                        onClick={() => setShowDatePicker(true)}
                        suffix={<CalendarOutlined style={{ color: '#666' }} />}
                      />
                    </div>
                  </Form.Item>

                  <Form.Item
                    name="notes"
                    label="Ghi chú"
                    className={styles.formItem}
                  >
                    <Input.TextArea
                      className={styles.textArea}
                      placeholder="Ghi chú (tùy chọn)"
                      rows={3}
                    />
                  </Form.Item>

                  {/* Wallet Linking Section */}
                  <Divider orientation="left">
                    <WalletOutlined /> Liên kết ví nguồn
                  </Divider>

                  <div className={styles.walletLinkingSection}>
                    {isEdit && walletLinks.length > 0 && (
                      <div className={styles.walletLinksList}>
                        <div className={styles.walletLinkingHeader}>
                          <WalletOutlined /> Ví đã liên kết
                        </div>
                        {walletLinks.map((link) => (
                          <div key={link.id} className={styles.walletLinkItem}>
                            <div className={styles.walletLinkContent}>
                              <div className={styles.walletLinkInfo}>
                                <div className={styles.walletLinkName}>
                                  {link.wallet_name} ({link.wallet_source_name})
                                </div>
                                <div className={styles.walletLinkAmount}>
                                  Số tiền: {formatCurrency(link.amount)}
                                </div>
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <span className={styles.walletLinkTag}>Đã liên kết</span>
                                <Button
                                  className={styles.unlinkButton}
                                  icon={<DeleteOutlined />}
                                  onClick={() => handleUnlinkWallet(link.id)}
                                  loading={loading}
                                >
                                  Hủy
                                </Button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    <div className={styles.walletLinkingForm}>
                      <div className={styles.walletLinkingHeader}>
                        <WalletOutlined /> Liên kết ví mới
                      </div>
                      <div className={styles.walletLinkingRow}>
                        <div className={styles.dateInputWrapper}>
                          <Input
                            className={styles.dateInput}
                            placeholder="Chọn ví"
                            value={selectedWallet ? getWalletDisplayName(selectedWallet) : ''}
                            readOnly
                            onClick={() => setShowWalletModal(true)}
                            suffix={<WalletOutlined style={{ color: '#666' }} />}
                          />
                        </div>
                        <InputNumber
                          className={styles.amountInput}
                          placeholder="Số tiền"
                          value={linkAmount}
                          onChange={(value) => setLinkAmount(value || 0)}
                          min={0}
                          max={transaction?.amount || 0}
                        />
                        <Button
                          className={styles.linkButton}
                          icon={<LinkOutlined />}
                          onClick={handleLinkWallet}
                          loading={loading}
                          disabled={!selectedWalletId || linkAmount <= 0}
                        >
                          Liên kết
                        </Button>
                      </div>
                      {linkAmount > (transaction?.amount || 0) && (
                        <div className={styles.amountError}>
                          <ExclamationCircleOutlined />
                          Số tiền liên kết không được vượt quá số tiền giao dịch
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}
            </Form>
          </div>

          <div className={styles.drawerFooter}>
            <div className={styles.drawerFooterRow}>
              <Button onClick={onClose} className={`${styles.drawerFooterButton} ${styles.default}`}>
                Hủy
              </Button>
              <Button
                type="primary"
                loading={loading}
                onClick={handleSubmit}
                className={`${styles.drawerFooterButton} ${styles.primary}`}
                disabled={isTransferTransaction && (!fromWalletId || !toWalletId || transferAmount <= 0 || fromWalletId === toWalletId || !checkWalletBalance(fromWalletId, transferAmount))}
              >
                {isTransferTransaction ? 'Chuyển tiền' : (isEdit ? 'Cập nhật' : 'Thêm')}
              </Button>
            </div>
          </div>
        </div>
      </Drawer>

      {/* Modal thêm danh mục mới */}
      <Drawer
        title="Thêm danh mục mới"
        open={showCategoryModal}
        onClose={() => setShowCategoryModal(false)}
        placement="bottom"
        height="70vh"
        className={styles.categoryDrawer}
        bodyStyle={{ 
          padding: 0,
          userSelect: 'none',
          WebkitUserSelect: 'none',
          MozUserSelect: 'none',
          msUserSelect: 'none',
          WebkitTouchCallout: 'none',
          WebkitTapHighlightColor: 'transparent'
        }}
      >
        <div className={styles.drawerContent}>
          <div className={styles.categoryDrawerBody}>
            <Form
              form={categoryForm}
              layout="vertical"
              initialValues={{
                icon: '💰',
                color: '#FF89A1',
              }}
            >
              <Form.Item
                name="name"
                label="Tên danh mục"
                rules={[{ required: true, message: 'Vui lòng nhập tên danh mục' }]}
                className={styles.formItem}
              >
                <Input placeholder="Nhập tên danh mục" />
              </Form.Item>

              <Form.Item
                name="icon"
                label="Icon"
                className={styles.formItem}
              >
                <Select className={styles.select} placeholder="Chọn icon">
                  {CATEGORY_ICONS.map((icon) => (
                    <Select.Option key={icon} value={icon}>
                      <div className={styles.iconOption}>
                        <span>{icon}</span>
                      </div>
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                name="color"
                label="Màu sắc"
                className={styles.formItem}
              >
                <Select className={styles.select} placeholder="Chọn màu sắc">
                  {CATEGORY_COLORS.map((color) => (
                    <Select.Option key={color} value={color}>
                      <div className={styles.colorOption}>
                        <div
                          className={styles.colorCircle}
                          style={{ backgroundColor: color }}
                        />
                        <span>{color}</span>
                      </div>
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Form>
          </div>

          <div className={styles.categoryDrawerFooter}>
            <div className={styles.categoryDrawerFooterRow}>
              <Button onClick={() => setShowCategoryModal(false)} className={`${styles.categoryDrawerFooterButton} ${styles.default}`}>
                Hủy
              </Button>
              <Button
                type="primary"
                loading={loading}
                onClick={handleAddCategory}
                className={`${styles.categoryDrawerFooterButton} ${styles.primary}`}
              >
                Thêm
              </Button>
            </div>
          </div>
        </div>
      </Drawer>

      {/* Custom Date Picker Modal */}
      <DatePickerModal
        isOpen={showDatePicker}
        onClose={() => setShowDatePicker(false)}
        onSelect={handleDateSelect}
        selectedDate={selectedDate}
        title={isTransferTransaction ? "Chọn ngày chuyển tiền" : "Chọn ngày chi tiêu"}
      />

      {/* Categories Modal */}
      <CategoriesModalMobile
        visible={showCategoriesModal}
        onClose={() => setShowCategoriesModal(false)}
        onSelectCategory={handleSelectCategory}
        onCreateNew={handleCreateNewCategory}
        categories={categories}
        onCategoryCreated={loadCategories}
      />

      {/* Wallet Selection Modal */}
      <SellectWalletModalMobile
        isOpen={showWalletModal}
        onClose={() => setShowWalletModal(false)}
        onSelect={handleWalletSelect}
        selectedWalletId={selectedWallet?.id}
      />
    </>
  );
};